/*
 * Copyright (c) CIRROM 2026.
 */

import React from "react";
import Typography from "@mui/material/Typography";
import Divider from "@mui/material/Divider";
import Link from "@mui/material/Link";
import Tooltip from "@mui/material/Tooltip";
import FolderIcon from "@mui/icons-material/Folder";
import LaunchIcon from "@mui/icons-material/Launch";
import DomainIcon from "@mui/icons-material/Domain";
import PublicIcon from "@mui/icons-material/Public";
import Report from "@mui/icons-material/Report";
import { DeliverabilityFact } from "./DeliverabilityFact";
import { CompletenessMeter, measureCompleteness } from "./CompletenessMeter";
import { buildLinkedInSearchUrl } from "../../utils/linkedinUtils";
import { useCrmVendor } from "./helpers/useCrmVendor";
import { encodeValue, toReadableText } from "../utils/utils";
import { RecordReviewModel } from "./helpers/normalize";
import classes from "./css/recordReview.module.css";

/*
 * The "informed decision" half of the surface: everything the review modals
 * squeeze into their icon header - provenance, deliverability, the duplicate
 * warning and the external lookups - laid out as a pane the steward can read
 * while the ledger beside it holds the decision. Identity lives in the card
 * header above, next to the Cirrom mark.
 */

const ExternalLink = (props: {
  href: string;
  label: string;
  icon: React.ReactNode;
}) => (
  <div className={classes.linkRow}>
    {props.icon}
    <Link
      href={props.href}
      target="_blank"
      rel="noopener"
      color="text.primary"
      underline="hover"
      variant="body2"
    >
      {props.label}
    </Link>
  </div>
);

export const DossierPane = (props: {
  model: RecordReviewModel;
  integratedSystemId: number;
}) => {
  const { model, integratedSystemId } = props;
  const { identity, links } = model;
  /*
   * The vendor is pure dressing here - a host outside the systems provider
   * loses the mark and the name, not the links.
   */
  const crmVendor = useCrmVendor(integratedSystemId);
  const completeness = measureCompleteness(
    model.ledgerFields,
    model.fullRecordFields
  );

  /*
   * External lookups per entity: a contact is searched by name (with the
   * employer as context), a company by its own name against LinkedIn's
   * company index. Either way the answer to "is this record real?" is one
   * click from the decision.
   */
  const searchUrls = (() => {
    if (model.entityType === "CONTACT") {
      if (!identity.firstName || !identity.lastName) {
        return null;
      }
      return {
        google: `https://www.google.com/search?q=${encodeValue(
          identity.firstName
        )} ${encodeValue(identity.lastName)} ${encodeValue(
          identity.company ?? ""
        )}`,
        linkedIn: buildLinkedInSearchUrl({
          firstName: identity.firstName,
          lastName: identity.lastName,
          company: identity.company,
        }),
      };
    }
    if (!identity.company) {
      return null;
    }
    return {
      google: `https://www.google.com/search?q=${encodeValue(
        identity.company
      )}`,
      linkedIn: `https://www.linkedin.com/search/results/companies/?keywords=${encodeURIComponent(
        identity.company
      )}`,
    };
  })();

  const iconSx = { color: "text.secondary" };

  /*
   * Two CRM rows, one destination. The row for the record being reviewed reads
   * "Open in HubSpot" and wears the vendor's mark - that pairing is what tells
   * the steward which CRM their approvals land in. The other row is the
   * neighbouring record, so it keeps the entity icon: a second HubSpot mark
   * would repeat what the first already said and drop the only distinction
   * that row carries. Without a recognised vendor both fall back to today's
   * wording and MUI icons.
   */
  const crmLink = (kind: "contact" | "account") => {
    const isRecordItself =
      kind === (model.entityType === "CONTACT" ? "contact" : "account");
    const entityIcon =
      kind === "contact" ? (
        <LaunchIcon className={classes.linkRowLogo} sx={iconSx} />
      ) : (
        <DomainIcon className={classes.linkRowLogo} sx={iconSx} />
      );
    if (!crmVendor) {
      return {
        label: kind === "contact" ? "Contact in CRM" : "Account in CRM",
        icon: entityIcon,
      };
    }
    const noun = kind === "contact" ? "Contact" : "Account";
    return {
      label: isRecordItself
        ? `Open in ${crmVendor.label}`
        : `${noun} in ${crmVendor.label}`,
      icon: isRecordItself ? (
        <img src={crmVendor.iconSrc} alt="" className={classes.linkRowLogo} />
      ) : (
        entityIcon
      ),
    };
  };

  const sections: React.ReactNode[] = [];

  /*
   * Leads the pane: it is the one fact here that changes when the steward
   * acts, and it sits directly opposite the ledger so "this record is
   * sparse" and "these changes fill it" read as one thought.
   */
  if (completeness) {
    sections.push(
      <CompletenessMeter
        completeness={completeness}
        crmLabel={crmVendor?.label}
        key="completeness"
      />
    );
  }

  if (identity.contactSource || model.sourceFolderName) {
    sections.push(
      <div className={classes.dossierSection} key="provenance">
        {identity.contactSource && (
          <Typography variant="body2" sx={{ color: "text.secondary" }}>
            Sourced from {toReadableText(identity.contactSource)}
          </Typography>
        )}
        {model.sourceFolderName && (
          <div className={classes.dossierFact}>
            <FolderIcon className={classes.dossierFactIcon} sx={iconSx} />
            <Typography variant="body2" sx={{ color: "text.secondary" }}>
              {model.sourceFolderName}
            </Typography>
          </div>
        )}
      </div>
    );
  }

  if (model.emailStatuses.length > 0) {
    sections.push(
      <div className={classes.dossierSection} key="deliverability">
        <Typography
          variant="body2"
          component="h3"
          sx={{ color: "text.secondary", fontWeight: 600 }}
        >
          Email deliverability
        </Typography>
        {model.emailStatuses.map((status) => (
          <DeliverabilityFact
            severity={status[0]}
            status={status[1]}
            key={`${status[0]}-${status[1]}`}
          />
        ))}
      </div>
    );
  }

  if (model.duplicateGroupId !== null) {
    sections.push(
      <div className={classes.dossierFact} key="duplicate">
        {/*
         * error.main, not a new colour: this is the same duplicate notice
         * the suggested-changes modal shows, kept identical so the two
         * surfaces never disagree about severity.
         */}
        <Report
          className={classes.dossierFactIcon}
          sx={{ color: "error.main" }}
        />
        <Link
          underline="always"
          color="text.secondary"
          variant="body2"
          sx={{ cursor: "pointer" }}
          onClick={() =>
            window.open(
              window.location.origin +
                `/app/duplicates/${integratedSystemId}?duplicateGroupId=${model.duplicateGroupId}`
            )
          }
        >
          {model.entityType === "CONTACT"
            ? "This contact has a duplicate record associated."
            : "This company has a duplicate record associated."}
        </Link>
      </div>
    );
  }

  sections.push(
    <div className={classes.dossierSection} key="links">
      <Typography
        variant="body2"
        sx={{ color: "text.secondary", fontWeight: 600 }}
      >
        Open elsewhere
      </Typography>
      {links.crmContact && (
        <ExternalLink
          href={links.crmContact}
          label={crmLink("contact").label}
          icon={crmLink("contact").icon}
        />
      )}
      {links.crmAccount && (
        <ExternalLink
          href={links.crmAccount}
          label={crmLink("account").label}
          icon={crmLink("account").icon}
        />
      )}
      {links.companyWebsite && (
        <ExternalLink
          href={links.companyWebsite}
          label="Company website"
          icon={<PublicIcon className={classes.linkRowLogo} sx={iconSx} />}
        />
      )}
      {(links.linkedInProfile || searchUrls) && (
        <ExternalLink
          href={links.linkedInProfile ?? searchUrls!.linkedIn}
          label={
            links.linkedInProfile ? "LinkedIn profile" : "Search on LinkedIn"
          }
          icon={
            <img
              src="/linkedin-logo.svg"
              alt=""
              className={classes.linkRowLogo}
            />
          }
        />
      )}
      {searchUrls && (
        <ExternalLink
          href={searchUrls.google}
          label="Search on Google"
          icon={
            <img
              src="/google-logo.png"
              alt=""
              className={classes.linkRowLogo}
            />
          }
        />
      )}
      {!links.crmContact &&
        !links.crmAccount &&
        !links.companyWebsite &&
        !links.linkedInProfile &&
        !searchUrls && (
          <Tooltip title="CRM links become available once the record's suggested changes load.">
            <Typography
              variant="body2"
              sx={{ color: "text.disabled", fontStyle: "italic" }}
            >
              none available
            </Typography>
          </Tooltip>
        )}
    </div>
  );

  return (
    <div className={classes.dossier}>
      {sections.map((section, index) => (
        <React.Fragment key={index}>
          {index > 0 && <Divider />}
          {section}
        </React.Fragment>
      ))}
    </div>
  );
};
